import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { GetAudienceAdminListResponse } from '~/services/PromoService';

type AudienceAdminRecord = GetAudienceAdminListResponse['result']['audienceList'][number];

type Props = {
  audience: AudienceAdminRecord;
};

function splitList(values?: Array<string>): string {
  return values?.join(', ') ?? '';
}

function getUtmSummary(config: AudienceAdminRecord['config']): string {
  const parts = [
    ['UTM source', splitList(config?.utm?.source)],
    ['UTM medium', splitList(config?.utm?.medium)],
    ['UTM campaign', splitList(config?.utm?.campaign)],
    ['UTM term', splitList(config?.utm?.term)],
  ]
    .filter(([, value]) => Boolean(value))
    .map(([label, value]) => `${label}: ${value}`);

  return parts.join(' | ');
}

export default function AudienceSummary({ audience }: Props) {
  const config = audience.config;
  const utmSummary = getUtmSummary(config);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Accordion disableGutters elevation={0} defaultExpanded={false} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6">Audience details</Typography>
            {utmSummary ? (
              <Typography variant="body2" color="text.secondary">
                {utmSummary}
              </Typography>
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={audience.enabled ? 'Enabled' : 'Disabled'}
                color={audience.enabled ? 'success' : 'default'}
              />
              <Chip label={audience.audience_type} />
              <Chip label={audience.sync_model} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Created by {audience.created_by} on {audience.created_at}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Audience ID: {audience.audience_id}
            </Typography>
            <Box>
              <Typography variant="subtitle2">UTM source</Typography>
              <Typography variant="body2">{splitList(config?.utm?.source) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM medium</Typography>
              <Typography variant="body2">{splitList(config?.utm?.medium) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM campaign</Typography>
              <Typography variant="body2">{splitList(config?.utm?.campaign) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">UTM term</Typography>
              <Typography variant="body2">{splitList(config?.utm?.term) || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">User type</Typography>
              <Typography variant="body2">{config?.userType || '—'}</Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
